import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { api, ApiError } from '@/lib/api';
import type { ClientWithdrawalMethod, ReferralStats } from '@/api/types';
import {
  WITHDRAW_METHODS,
  isPlatformMethod,
  withdrawAutoMaxError,
  plural,
  sortMethods,
  withdrawMinError,
} from '@/lib/client-withdraw';
import { useEffect, useState } from 'react';
import { getWebApp, haptic } from '@/lib/telegram';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Copy,
  Check,
  Share2,
  QrCode,
  ChevronDown,
  ChevronRight,
  Star,
  Coins,
  Trophy,
  HandCoins,
} from 'lucide-react';
import { toast } from 'sonner';
import * as QRCode from 'qrcode';

export const Route = createFileRoute('/referrals')({ component: Referrals });

function Referrals() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Без подстановки из кэша в queryFn: она возвращала закэшированное на каждый
  // invalidate, и после сохранения или решения по выводу экран показывал старое.
  const { data: stats } = useQuery({
    queryKey: ['referral-stats'],
    queryFn: () => api.referralStats(),
  });
  const { data: list } = useQuery({ queryKey: ['referrals'], queryFn: () => api.referralList() });

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton || !tg?.MainButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/profile' });
    tg.BackButton.onClick(onBack);
    tg.MainButton.hide();
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  const copyLink = async () => {
    if (!stats?.link) {
      toast.error(t('Copy fallback hint'));
      return;
    }
    const text = stats.link;
    const onOk = () => {
      setCopied(true);
      haptic('success');
      toast.success(t('Copied'));
      setTimeout(() => setCopied(false), 1500);
    };
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        onOk();
        return;
      }
      throw new Error('no clipboard');
    } catch {
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) {
          onOk();
          return;
        }
      } catch {
        /* fall through */
      }
      getWebApp()?.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(text)}`,
      );
    }
  };

  const shareLink = () => {
    if (!stats) return;
    getWebApp()?.openTelegramLink(
      `https://t.me/share/url?url=${encodeURIComponent(stats.link)}&text=${encodeURIComponent('Запусти свой VPN-сервис за 10 минут')}`
    );
  };

  if (!stats) return <ReferralsSkeleton />;

  return (
    <div className="p-4 space-y-3 pb-28">
      <h2 className="font-display text-2xl font-bold tracking-tight px-1 pt-2">{t('Referrals')}</h2>

      {stats.withdrawPending > 0 && <PendingWithdrawalsBanner count={stats.withdrawPending} />}

      {/* Сумма на всю ширину под двумя счётчиками: в трети экрана «6 568 ₽» обрезалось
          до «6 5…» уже на 375px, а деньги резать нельзя. */}
      <div className="grid grid-cols-2 gap-2.5">
        <Stat accent="primary" icon={<Users />} label={t('Ref invited')} value={(stats.invited ?? 0).toLocaleString('ru')} />
        <Stat accent="money" icon={<Star />} label={t('Ref active')} value={(stats.active ?? 0).toLocaleString('ru')} />
        <div className="col-span-2">
          <Stat accent="money" icon={<Coins />} label={t('Ref earned')} value={`${(stats.earnedRub ?? 0).toLocaleString('ru')}\u00a0₽`} />
        </div>
      </div>

      <SubpartnerCard />

      <div className="surface-card rounded-[22px] p-[18px] space-y-3">
        <h3 className="font-display font-semibold text-base">{t('Ref link')}</h3>
        {stats.link ? (
          <button
            type="button"
            onClick={copyLink}
            className="w-full surface-inset rounded-[14px] px-3.5 py-3 flex items-center gap-2 active:scale-[0.99] transition-transform text-left"
          >
            <code className="font-mono text-[11.5px] text-[#9fb8d6] truncate flex-1">{stats.link}</code>
            {copied ? <Check className="w-4 h-4 text-money-light shrink-0" /> : <Copy className="w-4 h-4 text-primary-light shrink-0" />}
          </button>
        ) : (
          <div className="w-full surface-inset rounded-[14px] px-3.5 py-3 text-xs text-muted-foreground">
            Ссылка появится после привязки франшизного бота
          </div>
        )}
        <button
          type="button"
          onClick={shareLink}
          className="w-full bg-primary text-primary-foreground py-3.5 rounded-[14px] text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
        >
          <Share2 className="w-4 h-4" />
          {t('Ref share')}
        </button>
        {stats.link && <RefQrBlock link={stats.link} />}
      </div>

      <ReferralSettingsCard stats={stats} />

      <TopReferrersCard />

      <div>
        <h3 className="eyebrow px-1 mb-2">{t('Ref list')}</h3>
        <div className="surface-card rounded-[20px] overflow-hidden divide-y divide-white/[0.05]">
          {list?.length === 0 ? (
            <div className="py-8 text-[13.5px] font-semibold text-[#6b6d75] text-center">Пока никого</div>
          ) : (
            list?.map((r) => (
              <div key={r.id} className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-[12px] bg-white/[0.07] text-primary-light flex items-center justify-center font-display font-bold shrink-0">
                  {r.firstName[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{r.firstName}</div>
                  <div className="text-[11px] text-dim">
                    L{r.level} · {new Date(r.joinedAt).toLocaleDateString('ru')}
                    {r.status === 'churned' && <span className="text-danger"> · отвалился</span>}
                  </div>
                </div>
                <div className="font-display font-bold text-sm nums whitespace-nowrap text-money-light">+{r.earnedRub} ₽</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Кто из клиентов реально приводит платящих. Сортировка по деньгам, а не по числу
 * приведённых: десять зевак стоят меньше одного покупателя, и партнёру важно
 * видеть именно это.
 */
function TopReferrersCard() {
  const [botId, setBotId] = useState<string>('');
  const { data: bots } = useQuery({ queryKey: ['bots'], queryFn: () => api.bots() });
  const { data: top, isPending } = useQuery({
    queryKey: ['referral-top', botId],
    queryFn: () => api.referralTop(botId || undefined),
  });

  return (
    <div>
      <div className="flex items-center gap-2 px-1 mb-2">
        <h3 className="eyebrow flex-1">Топ рефереров</h3>
        {bots && bots.length > 1 && (
          <select
            value={botId}
            onChange={(e) => {
              haptic('selection');
              setBotId(e.target.value);
            }}
            className="surface-card rounded-[10px] px-2.5 py-1 text-[11.5px] font-semibold outline-none max-w-[52%] truncate"
          >
            <option value="">Все боты</option>
            {bots.map((b) => (
              <option key={b.id} value={b.id}>
                {b.brandName || `@${b.username}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Скелетон снаружи карточки: SkeletonRows сам рисует карточки, внутри
          surface-card получилась бы вторая рамка поверх первой. */}
      {isPending ? (
        <SkeletonRows count={3} />
      ) : (
      <div className="surface-card rounded-[20px] overflow-hidden divide-y divide-white/[0.05]">
        {!top?.length ? (
          <div className="py-8 px-4 text-[13.5px] font-semibold text-[#6b6d75] text-center">
            Пока никто никого не привёл
          </div>
        ) : (
          top.map((r, i) => (
            <div key={r.id} className="p-4 flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-[10px] flex items-center justify-center font-display font-bold text-[13px] shrink-0 ${
                  i === 0
                    ? 'bg-money/[0.16] text-money-light'
                    : 'bg-white/[0.05] text-muted-foreground'
                }`}
              >
                {i === 0 ? <Trophy className="w-4 h-4" /> : i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{r.name}</div>
                <div className="text-[11px] text-dim">привёл {r.referredCount}</div>
              </div>
              <div className="font-display font-bold text-sm nums whitespace-nowrap text-money-light">
                +{r.earnedRub.toLocaleString('ru')} ₽
              </div>
            </div>
          ))
        )}
      </div>
      )}
    </div>
  );
}


/** Заявки клиентов на вывод ждут перевода: ведём прямо в раздел, где их отмечают. */
function PendingWithdrawalsBanner({ count }: { count: number }) {
  return (
    <Link
      to="/client-withdrawals"
      onClick={() => haptic('light')}
      className="flex items-center gap-3 rounded-[18px] p-[14px] border border-[rgba(245,166,35,0.28)] bg-[rgba(245,166,35,0.08)] active:scale-[0.99] transition-transform"
    >
      <div className="w-9 h-9 rounded-[11px] bg-[rgba(245,166,35,0.15)] text-[#f0b04e] flex items-center justify-center shrink-0">
        <HandCoins className="w-[18px] h-[18px]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold">
          Ждут перевода: {count} {plural(count, 'заявка', 'заявки', 'заявок')}
        </div>
        <div className="text-[11.5px] text-dim">Клиенты заказали вывод заработанного по рефералке</div>
      </div>
      <ChevronRight className="w-[18px] h-[18px] text-dim shrink-0" />
    </Link>
  );
}

function sameMethods(a: ClientWithdrawalMethod[], b: ClientWithdrawalMethod[]): boolean {
  return a.length === b.length && a.every((m) => b.includes(m));
}

function ReferralSettingsCard({ stats }: { stats: ReferralStats }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: partner } = useQuery({ queryKey: ['partner'], queryFn: () => api.me() });
  const [enabled, setEnabled] = useState(stats.enabled);
  const [l1, setL1] = useState<number>(stats.l1Percent);
  const [l2, setL2] = useState<number>(stats.l2Percent);
  // Вывод для клиентов. Карточка рисуется только после ответа, поэтому стартуем
  // с настоящих значений, а не с дефолтов (иначе «Сохранить» затёрло бы настройку).
  const [wEnabled, setWEnabled] = useState(stats.withdrawEnabled);
  const [wMin, setWMin] = useState(String(Math.round(stats.withdrawMinDisplayMinor / 100)));
  const [wMethods, setWMethods] = useState<ClientWithdrawalMethod[]>(sortMethods(stats.withdrawMethods));
  // Авто-выплата платформой (заявки #183, #186): GRAM и CryptoBot до порога без кнопки.
  const [wAuto, setWAuto] = useState(stats.withdrawAutoEnabled);
  const [wAutoMax, setWAutoMax] = useState(
    stats.withdrawAutoMaxDisplayMinor ? String(Math.round(stats.withdrawAutoMaxDisplayMinor / 100)) : '',
  );

  const withdraw = stats.withdrawAvailable;
  // Настройки вывода меняет только владелец: делегату бэк отвечает 403 на любое
  // поле withdraw_*. Показываем их выключенными и в запрос не кладём вовсе.
  const isDelegate = partner?.isDelegate === true;
  const wEditable = withdraw && !isDelegate;
  const wMinRub = Number(wMin);
  const wMinErr = withdrawMinError(wMin);
  const wMinBad = wMinErr !== null;
  const wNoMethods = wMethods.length === 0;
  const wHasPlatform = wMethods.some(isPlatformMethod);
  const wAutoMaxErr = wAuto ? withdrawAutoMaxError(wAutoMax) : null;
  const wAutoMaxRub = Number(wAutoMax);
  const wInvalid = wEditable && wEnabled && (wMinBad || wNoMethods || wAutoMaxErr !== null);

  const sumTooHigh = l1 + l2 > 100;
  const withdrawDirty =
    wEditable &&
    (wEnabled !== stats.withdrawEnabled ||
      (!wMinBad && wMinRub * 100 !== stats.withdrawMinDisplayMinor) ||
      !sameMethods(wMethods, stats.withdrawMethods) ||
      wAuto !== stats.withdrawAutoEnabled ||
      (wAuto && wAutoMaxErr === null && wAutoMaxRub * 100 !== stats.withdrawAutoMaxDisplayMinor));
  const dirty =
    enabled !== stats.enabled || l1 !== stats.l1Percent || l2 !== stats.l2Percent || withdrawDirty;

  const toggleMethod = (m: ClientWithdrawalMethod, on: boolean) => {
    haptic('selection');
    setWMethods((prev) => sortMethods(on ? [...prev, m] : prev.filter((x) => x !== m)));
  };

  const mut = useMutation({
    mutationFn: () =>
      api.updateReferralSettings({
        enabled,
        l1Percent: l1,
        l2Percent: l2,
        // Зеркалу и делегату поля вывода не шлём: бэк их у них не принимает.
        ...(wEditable
          ? {
              withdrawEnabled: wEnabled,
              // Невалидный минимум при выключенном выводе просто не трогаем.
              ...(wMinBad ? {} : { withdrawMinDisplayMinor: wMinRub * 100 }),
              withdrawMethods: wMethods,
              withdrawAutoEnabled: wAuto,
              ...(wAuto && wAutoMaxErr === null ? { withdrawAutoMaxDisplayMinor: wAutoMaxRub * 100 } : {}),
            }
          : {}),
      }),
    onSuccess: () => {
      haptic('success');
      toast.success(t('Saved'));
      qc.invalidateQueries({ queryKey: ['referral-stats'] });
    },
    onError: (e) => {
      haptic('error');
      // Текст бэка показываем, только если он по-русски (в том числе отказ делегату).
      const msg = e instanceof ApiError && /[А-Яа-яЁё]/.test(e.message) ? e.message : null;
      toast.error(msg ?? t('Save error'));
    },
  });

  const franchiseBot = partner?.franchiseBotUsername
    ? `@${partner.franchiseBotUsername}`
    : 'франшизного бота';

  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-base">{t('Ref settings')}</h3>
        <Switch checked={enabled} aria-label={t('Ref settings')} onCheckedChange={setEnabled} />
      </div>

      <PercentInput label={t('Ref L1')} value={l1} disabled={!enabled} onChange={setL1} />
      <PercentInput label={t('Ref L2')} value={l2} disabled={!enabled} onChange={setL2} />

      {sumTooHigh && <p className="text-[11px] text-danger">Сумма L1+L2 не может превышать 100%.</p>}
      <p className="text-[11.5px] text-dim leading-relaxed">
        Бонусы платятся из вашей маржи (наценка сверх платформенной цены). Считаются
        от вашей наценки, не от полной цены.
      </p>

      {withdraw && (
        <div className="pt-4 border-t border-white/[0.06] space-y-3.5">
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-display font-semibold text-[15px]">Вывод денег для клиентов</h4>
            <Switch
              checked={wEnabled}
              disabled={!wEditable}
              aria-label="Вывод денег для клиентов"
              onCheckedChange={(v) => {
                haptic('selection');
                setWEnabled(v);
              }}
            />
          </div>
          {isDelegate && (
            <p className="text-[11.5px] text-[#f0b04e] leading-relaxed">
              Настройки вывода меняет только владелец кабинета.
            </p>
          )}
          <p className="text-[11.5px] text-dim leading-relaxed">
            Клиенты смогут вывести заработанное по рефералке. Переводите деньги сами, по кнопке
            «Выплачено» сумма вернётся вам в доход. Заявки приходят в личку {franchiseBot} и в
            раздел «Выводы клиентов».
          </p>
          <p className="text-[11.5px] text-dim leading-relaxed">
            GRAM и чек CryptoBot может перевести платформа: кнопка «Выплатить через платформу» в
            заявке. Комиссия сети берётся из суммы клиента, ваш баланс не меняется: бонус уже
            списан с вашей маржи, когда клиент его заработал.
          </p>

          <label className="flex items-center justify-between gap-3">
            <span className="text-[13.5px] font-semibold text-[#cbd5e6]">Минимум заявки</span>
            <div className="flex items-center gap-2">
              <input
                type="text"
                inputMode="numeric"
                value={wMin}
                disabled={!wEnabled || !wEditable}
                onChange={(e) => setWMin(e.target.value.replace(/\D/g, '').slice(0, 7))}
                className="w-24 surface-inset rounded-[11px] px-3 py-2.5 text-center font-display text-[15px] font-bold nums disabled:opacity-40 outline-none focus:border-white/30"
              />
              <span className="text-sm font-bold text-dim">₽</span>
            </div>
          </label>
          {wEditable && wEnabled && wMinErr && (
            <p className="text-[11px] text-danger">{wMinErr}.</p>
          )}

          <div className="space-y-1">
            <div className="eyebrow">Способы вывода</div>
            {WITHDRAW_METHODS.map((m) => (
              <label
                key={m.value}
                className={`flex items-center justify-between gap-3 py-2 ${wEnabled && wEditable ? 'cursor-pointer' : ''}`}
              >
                <span className={`min-w-0 ${wEnabled && wEditable ? '' : 'opacity-40'}`}>
                  <span className="block text-[13.5px] font-semibold">{m.label}</span>
                  {isPlatformMethod(m.value) && (
                    <span className="block text-[11px] text-dim">
                      {stats.withdrawPlatformMethods.includes(m.value)
                        ? 'Может заплатить платформа'
                        : 'Платформа сейчас не платит этим способом, переводите сами'}
                    </span>
                  )}
                </span>
                <input
                  type="checkbox"
                  checked={wMethods.includes(m.value)}
                  disabled={!wEnabled || !wEditable}
                  onChange={(e) => toggleMethod(m.value, e.target.checked)}
                  className="w-5 h-5 accent-foreground disabled:opacity-40 shrink-0"
                />
              </label>
            ))}
          </div>
          {wEditable && wEnabled && wNoMethods && (
            <p className="text-[11px] text-danger">Выберите хотя бы один способ.</p>
          )}

          {wHasPlatform && (
            <div className="pt-3 border-t border-white/[0.06] space-y-2.5">
              <div className="flex items-center justify-between gap-3">
                <span className="text-[13.5px] font-semibold">Платформа платит сама</span>
                <Switch
                  checked={wAuto}
                  disabled={!wEnabled || !wEditable}
                  aria-label="Платформа платит сама"
                  onCheckedChange={(v) => {
                    haptic('selection');
                    setWAuto(v);
                    // Пустой порог при включении сразу горел бы ошибкой: берём подсказку.
                    if (v && !wAutoMax) setWAutoMax('1000');
                  }}
                />
              </div>
              <p className="text-[11.5px] text-dim leading-relaxed">
                Заявки в GRAM и CryptoBot до порога платформа выплатит без вашей кнопки. Крупные
                суммы и всплески по одному клиенту она сначала проверит сама.
              </p>
              <label className="flex items-center justify-between gap-3">
                <span className="text-[13.5px] font-semibold text-[#cbd5e6]">До суммы</span>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={wAutoMax}
                    placeholder="1000"
                    disabled={!wEnabled || !wEditable || !wAuto}
                    onChange={(e) => setWAutoMax(e.target.value.replace(/\D/g, '').slice(0, 7))}
                    className="w-24 surface-inset rounded-[11px] px-3 py-2.5 text-center font-display text-[15px] font-bold nums disabled:opacity-40 outline-none focus:border-white/30"
                  />
                  <span className="text-sm font-bold text-dim">₽</span>
                </div>
              </label>
              {wEditable && wEnabled && wAutoMaxErr && (
                <p className="text-[11px] text-danger">{wAutoMaxErr}.</p>
              )}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        disabled={!dirty || sumTooHigh || wInvalid || mut.isPending}
        onClick={() => mut.mutate()}
        className="w-full bg-primary text-primary-foreground py-3.5 rounded-[15px] text-sm font-bold active:scale-[0.985] transition-transform disabled:opacity-40 disabled:active:scale-100"
      >
        {mut.isPending ? '…' : t('Save')}
      </button>
    </div>
  );
}

function PercentInput({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3">
      <span className="text-[13.5px] font-semibold text-[#cbd5e6]">{label}</span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.5}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0);
          }}
          className="w-16 surface-inset rounded-[11px] px-3 py-2.5 text-center font-display text-[15px] font-bold nums disabled:opacity-40 outline-none focus:border-white/30"
        />
        <span className="text-sm font-bold text-dim">%</span>
      </div>
    </label>
  );
}

function ReferralsSkeleton() {
  return (
    <div className="p-4 pb-24 space-y-6">
      <Skeleton className="h-8 w-36 mt-2 mx-2 rounded-md" />

      {/* 3 stat cards */}
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-card border border-white/5 rounded-xl p-3 space-y-2">
            <Skeleton className="w-4 h-4 rounded-md" />
            <Skeleton className="h-3 w-16 rounded-md" />
            <Skeleton className="h-5 w-12 rounded-md" />
          </div>
        ))}
      </div>

      {/* Subpartner card */}
      <div className="bg-card border border-white/5 rounded-2xl p-5 space-y-3">
        <Skeleton className="h-5 w-44 rounded-md" />
        <div className="grid grid-cols-2 gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white/[0.03] rounded-xl p-3 space-y-2">
              <Skeleton className="h-4 w-20 rounded-md" />
              <Skeleton className="h-6 w-10 rounded-md" />
              <Skeleton className="h-3 w-16 rounded-md" />
            </div>
          ))}
        </div>
      </div>

      {/* Ref link card */}
      <div className="bg-card border border-white/5 rounded-2xl p-5 space-y-4">
        <Skeleton className="h-5 w-32 rounded-md" />
        <Skeleton className="h-12 w-full rounded-xl" />
        <Skeleton className="h-12 w-full rounded-xl" />
      </div>

      {/* Percent settings card */}
      <div className="bg-card border border-white/5 rounded-2xl p-5 space-y-3">
        <Skeleton className="h-5 w-40 rounded-md" />
        {[1, 2].map((i) => (
          <div key={i} className="flex items-center justify-between py-1.5">
            <Skeleton className="h-4 w-24 rounded-md" />
            <Skeleton className="h-4 w-10 rounded-md" />
          </div>
        ))}
      </div>

      {/* Ref list card */}
      <div>
        <Skeleton className="h-3 w-28 mx-2 mb-2 rounded-md" />
        <div className="bg-card border border-white/5 rounded-2xl divide-y divide-white/5 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-4 flex items-center gap-3">
              <Skeleton className="w-10 h-10 rounded-xl shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32 rounded-md" />
                <Skeleton className="h-3 w-24 rounded-md" />
              </div>
              <Skeleton className="h-4 w-12 rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function Stat({
  icon,
  label,
  value,
  accent = 'primary',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  accent?: 'primary' | 'money' | 'violet';
}) {
  const color = accent === 'money' ? 'text-money-light' : accent === 'violet' ? 'text-violet' : 'text-primary-light';
  return (
    <div className="surface-card rounded-[18px] p-[15px] flex flex-col gap-1">
      <div className={`${color} mb-1 [&_svg]:w-[18px] [&_svg]:h-[18px]`}>{icon}</div>
      <div className="eyebrow !text-[8.5px]">{label}</div>
      <div className="font-display text-[22px] font-bold nums [overflow-wrap:anywhere]">{value}</div>
    </div>
  );
}

function SubpartnerCard() {
  const { data } = useQuery({ queryKey: ['subpartner-stats'], queryFn: () => api.subpartnerStats() });
  if (!data) return null;
  const l1Usd = (data.l1.bonus_earned_minor / 100).toFixed(2);
  const l2Usd = (data.l2.bonus_earned_minor / 100).toFixed(2);
  return (
    <div className="surface-card rounded-[22px] p-[18px] space-y-3.5">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold text-[17px]">Субпартнёрская программа</h3>
        <span className="font-mono text-xs text-dim">₽ → ₽</span>
      </div>
      <div className="grid grid-cols-2 gap-2.5">
        <SubpartnerLevel
          level="L1"
          accent="primary"
          label="прямые"
          count={data.l1.referred_count}
          earned={l1Usd}
          currency={data.currency}
        />
        <SubpartnerLevel
          level="L2"
          accent="violet"
          label="их рефералы"
          count={data.l2.referred_count}
          earned={l2Usd}
          currency={data.currency}
        />
      </div>
      <p className="text-[11.5px] text-dim leading-relaxed">
        Бонус начисляется с платформенной маржи в каждой их продаже. Деньги в кошельке, можно вывести.
      </p>
    </div>
  );
}

function SubpartnerLevel({
  level,
  accent,
  label,
  count,
  earned,
  currency,
}: {
  level: string;
  accent: 'primary' | 'violet';
  label: string;
  count: number;
  earned: string;
  currency: string;
}) {
  const badge =
    accent === 'violet'
      ? 'text-violet bg-[rgba(159,122,234,0.16)]'
      : 'text-primary-light bg-white/[0.07]';
  return (
    <div className="surface-inset rounded-[15px] p-3.5">
      <div className="flex items-center gap-2">
        <span className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded ${badge}`}>{level}</span>
        <span className="text-[10.5px] text-dim truncate">{label}</span>
      </div>
      <div className="font-display text-[22px] font-bold nums mt-2.5">{count}</div>
      <div className="text-[11px] text-money-light mt-0.5">+{earned} {currency}</div>
    </div>
  );
}

/**
 * Block B.2: collapsible QR-код реф-ссылки. SVG генерируется через qrcode lib.
 * По клику на «Скачать» — генерируем PNG dataURL и триггерим download.
 */
function RefQrBlock({ link }: { link: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [svg, setSvg] = useState<string | null>(null);

  useEffect(() => {
    if (!open || svg) return;
    void QRCode.toString(link, { type: 'svg', errorCorrectionLevel: 'M', margin: 1 }).then(setSvg);
  }, [open, svg, link]);

  const handleDownload = async () => {
    haptic('selection');
    const dataUrl = await QRCode.toDataURL(link, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 1024,
    });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'referral-qr.png';
    a.click();
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-white/5 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-white/[0.08] transition-colors"
      >
        <QrCode className="w-4 h-4" />
        {t('Ref QR title')}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="bg-white p-4 rounded-xl flex flex-col items-center gap-3">
          {svg ? (
            <div
              className="w-48 h-48"
              dangerouslySetInnerHTML={{ __html: svg }}
            />
          ) : (
            <Skeleton tone="light" className="w-48 h-48 rounded" />
          )}
          <button
            type="button"
            onClick={handleDownload}
            className="text-xs font-bold text-foreground"
          >
            {t('Ref QR download')} ↓
          </button>
        </div>
      )}
    </div>
  );
}
